import React, {ReactElement} from 'react'
import {FormattedDate} from 'react-intl'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {guestPickerMessages} from '../../search/GuestPicker/guestPickerMessages'
import {
  GuestSplit,
  GuestSplitText
} from '../../search/GuestPicker/GuestSplitText'

interface AvailableDealsSectionProps {
  checkIn: string
  checkOut: string
  numberOfGuests: number
  numberOfRooms: number
  /** When set, shows "N rooms · X adults, Y children" instead of the guest total. */
  guestSplit?: GuestSplit
  numberOfOffers: number
  roomOffersListSlot: React.ReactNode
}

const RoomOffersListWrapper = styled.div`
  min-width: 0;
  width: 100%;
`

const RoomGuestsText = ({
  numberOfGuests,
  numberOfRooms,
  guestSplit
}: Pick<
  AvailableDealsSectionProps,
  'numberOfGuests' | 'numberOfRooms' | 'guestSplit'
>): ReactElement => {
  if (guestSplit) {
    return (
      <>
        <FormattedMessageWrapper
          {...guestPickerMessages.room}
          values={{count: numberOfRooms}}
        />
        {' · '}
        <GuestSplitText {...guestSplit} />
      </>
    )
  }
  return (
    <>
      <FormattedMessageWrapper
        {...guestPickerMessages.guest}
        values={{count: numberOfGuests}}
      />
      {', '}
      <FormattedMessageWrapper
        {...guestPickerMessages.room}
        values={{count: numberOfRooms}}
      />
    </>
  )
}

export const AvailableDealsSection = ({
  availableDealsSectionProps
}: {
  availableDealsSectionProps: AvailableDealsSectionProps
}): ReactElement => {
  const {
    checkIn,
    checkOut,
    numberOfGuests,
    numberOfRooms,
    guestSplit,
    numberOfOffers,
    roomOffersListSlot
  } = availableDealsSectionProps
  const {isMobile} = useDeviceLayout()
  return (
    <Box display="flex" flexDirection="column" gap="s500">
      <Box
        display="flex"
        gap="s200"
        flexDirection="column"
        paddingLeft={isMobile ? 's500' : 's000'}
        paddingRight={isMobile ? 's500' : 's000'}
      >
        <Text variant="titleL">
          <FormattedMessageWrapper
            id="availableDeals"
            defaultMessage="Available deals ({count})"
            values={{count: numberOfOffers}}
          />
        </Text>
        <Box display="flex" alignItems="center">
          <Text variant="bodyM">
            <FormattedDate
              value={checkIn}
              month="short"
              day="2-digit"
              timeZone="UTC"
            />
            {' - '}
            <FormattedDate
              value={checkOut}
              month="short"
              day="2-digit"
              timeZone="UTC"
            />
            {' · '}
            <RoomGuestsText
              numberOfGuests={numberOfGuests}
              numberOfRooms={numberOfRooms}
              guestSplit={guestSplit}
            />
          </Text>
        </Box>
      </Box>
      <RoomOffersListWrapper>{roomOffersListSlot}</RoomOffersListWrapper>
    </Box>
  )
}
